import React, { useEffect, useMemo, useState } from "react";
import SmsConsentNote from "../../components/SmsConsentNote";
import { useParams } from "react-router-dom";
import { Alert, Button, Card, Input, Layout, Result, Spin, Steps, Typography, message } from "antd";
import dayjs, { Dayjs } from "dayjs";
import axiosClient from "../../api/axiosClient";

const { Content } = Layout;
const { Title, Text } = Typography;

type Service = {
  service_id: string;
  name: string;
  duration_min: number;
  price: number;
  description: string;
};

type Staff = {
  user_id: string;
  name: string;
};

type BusinessInfo = {
  business_id: string;
  page_title: string;
  page_description: string;
  logo_url?: string;
  tagline?: string;
  hidden_steps?: string[];
  services: Service[];
  staff: Staff[];
  waitlist_enabled?: boolean;
  waitlist_notice_minutes?: number;
};

function formatMoney(n: number) {
  return n.toLocaleString(undefined, { style: "currency", currency: "USD" });
}

function formatNoticeTime(minutes: number): string {
  if (minutes <= 0) return "no minimum notice";
  if (minutes < 60) return `${minutes} min`;
  const hours = minutes / 60;
  return `${hours % 1 === 0 ? hours : hours.toFixed(1)} hr${hours !== 1 ? "s" : ""}`;
}

type Step = "service" | "staff" | "datetime" | "details" | "confirmation";

export default function PublicBookingPage() {
  const { slug } = useParams<{ slug: string }>();
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [business, setBusiness] = useState<BusinessInfo | null>(null);

  const [step, setStep] = useState<Step>("service");
  const [selectedService, setSelectedService] = useState<Service | null>(null);
  const [selectedStaff, setSelectedStaff] = useState<Staff | null>(null);
  const [selectedDate, setSelectedDate] = useState<Dayjs>(dayjs());
  const [slots, setSlots] = useState<string[]>([]);
  const [slotsLoading, setSlotsLoading] = useState(false);
  const [selectedSlot, setSelectedSlot] = useState<string | null>(null);

  const [name, setName] = useState("");
  const [phone, setPhone] = useState("");
  const [email, setEmail] = useState("");
  const [notes, setNotes] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [bookingId, setBookingId] = useState<string | null>(null);

  const [joiningWaitlist, setJoiningWaitlist] = useState(false);
  const [waitlistSubmitting, setWaitlistSubmitting] = useState(false);
  const [waitlistJoined, setWaitlistJoined] = useState(false);

  useEffect(() => {
    if (!slug) return;
    axiosClient
      .get("/public/business", { params: { slug } })
      .then((res) => setBusiness(res.data))
      .catch((err) => setLoadError(err.response?.data || "This booking page isn't available."))
      .finally(() => setLoading(false));
  }, [slug]);

  useEffect(() => {
    if (step !== "datetime" || !business || !selectedService || !selectedStaff) return;
    setSlotsLoading(true);
    setSelectedSlot(null);
    axiosClient
      .get("/public/availability", {
        params: {
          business_id: business.business_id,
          service_id: selectedService.service_id,
          staff_id: selectedStaff.user_id,
          date: selectedDate.format("YYYY-MM-DD"),
        },
      })
      .then((res) => setSlots(res.data.slots ?? []))
      .catch(() => setSlots([]))
      .finally(() => setSlotsLoading(false));
    setJoiningWaitlist(false);
    setWaitlistJoined(false);
  }, [step, business, selectedService, selectedStaff, selectedDate]);

  const nextDays = useMemo(() => Array.from({ length: 14 }, (_, i) => dayjs().add(i, "day")), []);

  const hiddenSteps = business?.hidden_steps ?? [];
  const staffStepHidden = hiddenSteps.includes("staff");
  const notesStepHidden = hiddenSteps.includes("notes");

  function chooseService(s: Service) {
    setSelectedService(s);
    if (staffStepHidden && business && business.staff.length > 0) {
      setSelectedStaff(business.staff[0]);
      setStep("datetime");
    } else {
      setStep("staff");
    }
  }

  async function handleJoinWaitlist() {
    if (!business || !selectedService || !name.trim()) return;
    setWaitlistSubmitting(true);
    try {
      await axiosClient.post("/public/waitlist", {
        business_id: business.business_id,
        name,
        phone,
        service: selectedService.name,
      });
      setWaitlistJoined(true);
    } catch (err: any) {
      message.error(err.response?.data || "Failed to join the waitlist — please try again.");
    } finally {
      setWaitlistSubmitting(false);
    }
  }

  async function handleConfirmBooking() {
    if (!business || !selectedService || !selectedStaff || !selectedSlot) return;
    setSubmitting(true);
    try {
      const res = await axiosClient.post("/public/bookings", {
        business_id: business.business_id,
        service_id: selectedService.service_id,
        staff_id: selectedStaff.user_id,
        start_time: selectedSlot,
        name,
        phone,
        email,
        notes,
      });
      setBookingId(res.data.booking_id);
      setStep("confirmation");
    } catch (err: any) {
      message.error(err.response?.data || "That time is no longer available — please pick another.");
    } finally {
      setSubmitting(false);
    }
  }

  if (loading) {
    return (
      <Layout style={{ minHeight: "100vh", display: "flex", alignItems: "center", justifyContent: "center" }}>
        <Spin size="large" />
      </Layout>
    );
  }

  if (loadError || !business) {
    return (
      <Layout style={{ minHeight: "100vh" }}>
        <Content style={{ display: "flex", alignItems: "center", justifyContent: "center", height: "100vh" }}>
          <Result status="404" title="Booking page not found" subTitle={loadError ?? undefined} />
        </Content>
      </Layout>
    );
  }

  const visibleStepIds = ["service", ...(staffStepHidden ? [] : ["staff"]), "datetime", "details"];
  const stepIndex = visibleStepIds.indexOf(step);

  return (
    <Layout style={{ minHeight: "100vh", background: "#f5f5f5" }}>
      <Content style={{ padding: 24 }}>
        <div style={{ maxWidth: 720, margin: "0 auto" }}>
          <div style={{ textAlign: "center", marginBottom: 24 }}>
            {business.logo_url && (
              <img
                src={business.logo_url}
                alt=""
                style={{ width: 64, height: 64, borderRadius: 12, objectFit: "contain", marginBottom: 8 }}
              />
            )}
            <Title level={2}>{business.page_title || "Book an Appointment"}</Title>
            {business.tagline && (
              <div style={{ color: "#2563eb", marginBottom: 4 }}>{business.tagline}</div>
            )}
            {business.page_description && <Text type="secondary">{business.page_description}</Text>}
          </div>

          {step !== "confirmation" && (
            <Steps
              current={stepIndex}
              size="small"
              style={{ marginBottom: 24 }}
              items={[
                { title: "Service" },
                ...(staffStepHidden ? [] : [{ title: "Stylist" }]),
                { title: "Date & Time" },
                { title: "Details" },
              ]}
            />
          )}

          {step === "service" && (
            <div style={{ display: "grid", gap: 12 }}>
              {business.services.map((s) => (
                <Card
                  key={s.service_id}
                  hoverable
                  onClick={() => chooseService(s)}
                >
                  <div style={{ display: "flex", justifyContent: "space-between" }}>
                    <div>
                      <Text strong>{s.name}</Text>
                      <div>
                        <Text type="secondary">{s.duration_min} min</Text>
                      </div>
                    </div>
                    <Text strong>{formatMoney(s.price)}</Text>
                  </div>
                </Card>
              ))}
              {business.services.length === 0 && <Alert type="info" message="No services are available to book right now." />}
            </div>
          )}

          {step === "staff" && (
            <div>
              <Button type="link" onClick={() => setStep("service")} style={{ paddingLeft: 0 }}>
                ← Change Service
              </Button>
              <div style={{ display: "grid", gap: 12 }}>
                {business.staff.map((s) => (
                  <Card
                    key={s.user_id}
                    hoverable
                    onClick={() => {
                      setSelectedStaff(s);
                      setStep("datetime");
                    }}
                  >
                    <Text strong>{s.name}</Text>
                  </Card>
                ))}
                {business.staff.length === 0 && <Alert type="info" message="No stylists are available to book right now." />}
              </div>
            </div>
          )}

          {step === "datetime" && selectedService && selectedStaff && (
            <div>
              <Button
                type="link"
                onClick={() => setStep(staffStepHidden ? "service" : "staff")}
                style={{ paddingLeft: 0 }}
              >
                ← {staffStepHidden ? "Change Service" : "Change Stylist"}
              </Button>
              <Text type="secondary">
                {selectedService.name} with {selectedStaff.name}
              </Text>

              <div style={{ display: "flex", gap: 8, overflowX: "auto", margin: "16px 0" }}>
                {nextDays.map((d) => (
                  <Button
                    key={d.format("YYYY-MM-DD")}
                    type={selectedDate.isSame(d, "day") ? "primary" : "default"}
                    onClick={() => setSelectedDate(d)}
                    style={{ flex: "0 0 auto" }}
                  >
                    {d.format("ddd M/D")}
                  </Button>
                ))}
              </div>

              {slotsLoading ? (
                <Spin />
              ) : slots.length === 0 ? (
                <div>
                  <Alert type="info" message="No available times on this date — try another day." />
                  {business.waitlist_enabled && (
                    waitlistJoined ? (
                      <Alert
                        type="success"
                        showIcon
                        style={{ marginTop: 12 }}
                        message="You're on the waitlist!"
                        description={`We'll reach out as soon as a slot opens up${(business.waitlist_notice_minutes ?? 0) > 0 ? ` with at least ${formatNoticeTime(business.waitlist_notice_minutes ?? 0)} notice` : ""}.`}
                      />
                    ) : joiningWaitlist ? (
                      <div style={{ marginTop: 12, padding: 16, border: "1px solid #f0f0f0", borderRadius: 8 }}>
                        <Text strong>Join the waitlist</Text>
                        <p style={{ marginTop: 4 }}>
                          <Text type="secondary">
                            We'll contact you as soon as a slot opens up
                            {(business.waitlist_notice_minutes ?? 0) > 0 ? ` (at least ${formatNoticeTime(business.waitlist_notice_minutes ?? 0)} notice)` : ""}.
                          </Text>
                        </p>
                        <Input placeholder="Full Name *" value={name} onChange={(e) => setName(e.target.value)} style={{ marginBottom: 8, marginTop: 8 }} />
                        <Input placeholder="Phone" value={phone} onChange={(e) => setPhone(e.target.value)} style={{ marginBottom: 8 }} />
                        <SmsConsentNote />
                        <div style={{ display: "flex", gap: 8 }}>
                          <Button type="primary" loading={waitlistSubmitting} disabled={!name.trim()} onClick={handleJoinWaitlist}>
                            Join Waitlist
                          </Button>
                          <Button onClick={() => setJoiningWaitlist(false)}>Cancel</Button>
                        </div>
                      </div>
                    ) : (
                      <Button style={{ marginTop: 12 }} onClick={() => setJoiningWaitlist(true)}>
                        Join Waitlist
                      </Button>
                    )
                  )}
                </div>
              ) : (
                <div style={{ display: "flex", flexWrap: "wrap", gap: 8 }}>
                  {slots.map((s) => (
                    <Button
                      key={s}
                      type={selectedSlot === s ? "primary" : "default"}
                      onClick={() => setSelectedSlot(s)}
                    >
                      {dayjs(s).format("h:mm A")}
                    </Button>
                  ))}
                </div>
              )}

              <Button
                type="primary"
                block
                size="large"
                style={{ marginTop: 20 }}
                disabled={!selectedSlot}
                onClick={() => setStep("details")}
              >
                Continue to Details
              </Button>
            </div>
          )}

          {step === "details" && selectedService && selectedStaff && selectedSlot && (
            <div>
              <Button type="link" onClick={() => setStep("datetime")} style={{ paddingLeft: 0 }}>
                ← Change Date & Time
              </Button>
              <Card style={{ marginBottom: 16 }}>
                <Text strong>{selectedService.name}</Text> with <Text strong>{selectedStaff.name}</Text>
                <div>
                  <Text type="secondary">
                    {dayjs(selectedSlot).format("dddd, MMMM D [at] h:mm A")} · {formatMoney(selectedService.price)}
                  </Text>
                </div>
              </Card>

              <Input placeholder="Full Name *" value={name} onChange={(e) => setName(e.target.value)} style={{ marginBottom: 12 }} />
              <Input placeholder="Email *" value={email} onChange={(e) => setEmail(e.target.value)} style={{ marginBottom: 12 }} />
              <Input placeholder="Phone *" value={phone} onChange={(e) => setPhone(e.target.value)} style={{ marginBottom: 8 }} />
              <SmsConsentNote />
              {!notesStepHidden && (
                <Input.TextArea placeholder="Notes (optional)" value={notes} onChange={(e) => setNotes(e.target.value)} style={{ marginBottom: 12 }} />
              )}

              <Button
                type="primary"
                block
                size="large"
                loading={submitting}
                disabled={!name || (!phone && !email)}
                onClick={handleConfirmBooking}
              >
                Confirm Booking
              </Button>
            </div>
          )}

          {step === "confirmation" && selectedService && selectedStaff && selectedSlot && (
            <Result
              status="success"
              title="You're booked!"
              subTitle={
                <>
                  {selectedService.name} with {selectedStaff.name} on {dayjs(selectedSlot).format("dddd, MMMM D [at] h:mm A")}
                  {bookingId && (
                    <div style={{ marginTop: 8 }}>
                      <Text type="secondary" style={{ fontFamily: "monospace" }}>
                        Confirmation: {bookingId}
                      </Text>
                    </div>
                  )}
                </>
              }
              extra={
                <Button
                  type="primary"
                  onClick={() => {
                    setStep("service");
                    setSelectedService(null);
                    setSelectedStaff(null);
                    setSelectedSlot(null);
                    setName("");
                    setPhone("");
                    setEmail("");
                    setNotes("");
                    setBookingId(null);
                  }}
                >
                  Book Another
                </Button>
              }
            />
          )}
        </div>
      </Content>
    </Layout>
  );
}
